import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader, { absUrl } from "../../components/admin/ImageUploader";

const BADGES = [
  { value: "", label: "None" },
  { value: "New", label: "New" },
  { value: "Best Seller", label: "Best Seller" },
  { value: "Limited", label: "Limited" },
  { value: "Sale", label: "Sale" },
];

const FALLBACK_SIZES = ["XS", "S", "M", "L", "XL"];

const makeEmpty = (sizes) => ({
  name: "", slug: "", price: 1990, original_price: 0, category: "",
  fabric: "", description: "",
  badge: "", images: [""], sizes,
  stock: Object.fromEntries(sizes.map((s) => [s, 25])),
  rating: 4.8, reviews: 0, active: true,
});

function slugify(name) {
  return (name || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [adminSizes, setAdminSizes] = useState(FALLBACK_SIZES);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, s] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/categories"),
        api.get("/admin/settings").catch(() => ({ data: {} })),
      ]);
      setProducts(p.data);
      setCategories(c.data);
      const rows = s.data?.size_chart?.rows || [];
      const sizes = rows.map((r) => r.size).filter(Boolean);
      setAdminSizes(sizes.length ? sizes : FALLBACK_SIZES);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    setSaving(true);
    try {
      const sizes = form.sizes && form.sizes.length ? form.sizes : adminSizes;
      const payload = {
        ...form,
        original_price: form.original_price ? Number(form.original_price) : null,
        price: Number(form.price),
        rating: Number(form.rating),
        reviews: Number(form.reviews),
        images: form.images.filter(Boolean),
        sizes,
        stock: Object.fromEntries(
          sizes.map((s) => [s, Number(form.stock?.[s] ?? 0)])
        ),
      };
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totalStock = (s) => Object.values(s || {}).reduce((a, b) => a + Number(b || 0), 0);

  return (
    <div className="p-6 md:p-10" data-testid="admin-products">
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <span className="font-script text-3xl text-blush-400">the catalogue</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Products</h1>
        </div>
        <button
          className="btn-maroon inline-flex items-center gap-2"
          onClick={() => setEditing("new")}
          data-testid="admin-add-product-btn"
        >
          <Plus size={14} strokeWidth={1.6} /> New product
        </button>
      </header>

      <div className="rounded-3xl bg-white border border-blush-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body min-w-[680px]">
            <thead>
              <tr className="bg-blush-50/40 text-left text-[11px] uppercase tracking-[0.18em] text-ink-500">
                <th className="px-4 py-3 w-12 text-center">#</th>
                <th className="px-6 py-3">Product</th>
                <th className="px-6 py-3">Category</th>
                <th className="px-6 py-3">Price</th>
                <th className="px-6 py-3">Stock</th>
                <th className="px-6 py-3">Active</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-ink-500">Loading…</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-ink-500">No products yet.</td></tr>
              ) : products.map((p, idx) => (
                <tr key={p.id} className="border-t border-blush-100 hover:bg-blush-50/40">
                  <td className="px-4 py-3 text-center text-ink-500 text-xs">{idx + 1}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && (
                        <img src={absUrl(p.images[0])} alt="" className="h-10 w-10 object-cover rounded-lg" />
                      )}
                      <div>
                        <p className="text-ink-900">{p.name}</p>
                        <p className="text-[11px] text-ink-500">{p.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 capitalize">{p.category}</td>
                  <td className="px-6 py-3">₹{p.price.toLocaleString("en-IN")}</td>
                  <td className="px-6 py-3">{totalStock(p.stock)}</td>
                  <td className="px-6 py-3">{p.active ? "✓" : "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => setEditing(p)}
                      className="h-8 w-8 inline-grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
                      data-testid={`admin-edit-${p.id}`}
                    >
                      <Pencil size={14} strokeWidth={1.6} />
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      className="h-8 w-8 inline-grid place-items-center rounded-full hover:bg-blush-50 text-blush-600"
                      data-testid={`admin-delete-${p.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ProductDrawer
          initial={editing === "new" ? makeEmpty(adminSizes) : editing}
          categories={categories}
          adminSizes={adminSizes}
          onSave={onSave}
          onClose={() => setEditing(null)}
          saving={saving}
          isNew={editing === "new"}
        />
      )}
    </div>
  );
}

function ProductDrawer({ initial, categories, adminSizes, onSave, onClose, saving, isNew }) {
  const base = { ...makeEmpty(adminSizes), ...initial };
  // The category dropdown falls back to displaying its first <option>
  // whenever its bound value doesn't match any real category (e.g. no
  // category picked yet, or a product's category was since renamed/
  // deleted) — silently, since a <select> never reflects that mismatch
  // visually. Keep the underlying state in sync with what's actually
  // showing, so what you see is always what gets submitted.
  if (!categories.some((c) => c.slug === base.category)) {
    base.category = categories[0]?.slug || "";
  }
  // Use admin-configured sizes (from size chart). Preserve product's stock for sizes still in use.
  const sizes = adminSizes.length ? adminSizes : FALLBACK_SIZES;
  const stock = Object.fromEntries(sizes.map((s) => [s, Number(base.stock?.[s] ?? 0)]));
  const [f, setF] = useState({
    ...base,
    sizes,
    stock,
    images: base.images?.length ? base.images : [""],
  });
  const [slugTouched, setSlugTouched] = useState(!isNew);

  const update = (k, v) => {
    setF((s) => {
      const next = { ...s, [k]: v };
      if (k === "name" && !slugTouched) {
        next.slug = slugify(v);
      }
      return next;
    });
  };
  const setSlug = (v) => {
    setSlugTouched(true);
    setF((s) => ({ ...s, slug: v }));
  };
  const setImg = (i, v) => setF((s) => ({ ...s, images: s.images.map((x, idx) => (idx === i ? v : x)) }));
  const addImg = () => setF((s) => ({ ...s, images: [...s.images, ""] }));
  const rmImg = (i) => setF((s) => ({ ...s, images: s.images.filter((_, idx) => idx !== i) }));
  const setStock = (size, v) => setF((s) => ({ ...s, stock: { ...s.stock, [size]: Number(v || 0) } }));

  const totalStock = Object.values(f.stock).reduce((a, b) => a + Number(b || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        className="w-full md:max-w-2xl bg-cream-100 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-product-drawer"
      >
        <div className="sticky top-0 z-10 bg-cream-100/95 backdrop-blur border-b border-blush-100 px-6 md:px-8 py-5 flex items-center justify-between">
          <h2 className="font-heading text-xl md:text-2xl text-ink-900">
            {isNew ? "New product" : "Edit product"}
          </h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center hover:bg-blush-50">
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); onSave(f); }}
          className="p-6 md:p-8 space-y-5"
          data-testid="admin-product-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" required value={f.name} onChange={(v) => update("name", v)} />
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Slug</label>
              <input value={f.slug} onChange={(e) => setSlug(e.target.value)} required placeholder="auto-generated from name" className="field mt-2" />
              <p className="mt-1 text-[11px] font-body text-ink-500">Auto-fills from name. Edit to override.</p>
            </div>
            <Field label="Price (INR)" type="number" required value={f.price} onChange={(v) => update("price", v)} />
            <Field label="Original price (INR)" type="number" value={f.original_price} onChange={(v) => update("original_price", v)} />
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Category</label>
              <select value={f.category} onChange={(e) => update("category", e.target.value)} className="field mt-2 capitalize" data-testid="product-category-select">
                {categories.map((c) => (
                  <option key={c.id} value={c.slug} className="capitalize">{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Badge</label>
              <select value={f.badge || ""} onChange={(e) => update("badge", e.target.value)} className="field mt-2" data-testid="product-badge-select">
                {BADGES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            <Field label="Rating" type="number" step="0.1" value={f.rating} onChange={(v) => update("rating", v)} />
            <Field label="Reviews count" type="number" value={f.reviews} onChange={(v) => update("reviews", v)} />
          </div>
          <Field label="Fabric" value={f.fabric} onChange={(v) => update("fabric", v)} />
          <Field label="Description" textarea value={f.description} onChange={(v) => update("description", v)} />

          {/* Stock per size */}
          <div className="rounded-2xl bg-white border border-blush-100 p-5">
            <div className="flex items-end justify-between mb-3">
              <div>
                <h3 className="font-heading text-lg text-ink-900">Stock per size</h3>
                <p className="text-[11px] font-body text-ink-500">Sizes are managed in Settings → Size chart. Add or remove rows there to change this list.</p>
              </div>
              <span className="text-xs font-body text-ink-500">
                Total: <span className="text-ink-900 font-medium">{totalStock}</span>
              </span>
            </div>
            <div className={`grid gap-3 ${f.sizes.length <= 5 ? "grid-cols-5" : "grid-cols-3 md:grid-cols-6"}`}>
              {f.sizes.map((s) => (
                <div key={s}>
                  <label className="text-xs font-body uppercase tracking-[0.2em] text-blush-600">{s}</label>
                  <input
                    type="number"
                    min="0"
                    value={f.stock[s] ?? 0}
                    onChange={(e) => setStock(s, e.target.value)}
                    className="field mt-2 text-center !py-2 !px-3"
                    data-testid={`stock-${s}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div>
            <p className="text-xs font-body uppercase tracking-[0.2em] text-ink-500 mb-3">
              Images
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {f.images.map((src, i) => (
                <ImageUploader
                  key={i}
                  value={src}
                  onChange={(url) => setImg(i, url)}
                  onRemove={() => rmImg(i)}
                />
              ))}
              <button
                type="button"
                onClick={addImg}
                className="h-32 rounded-xl border-2 border-dashed border-blush-200 text-xs uppercase tracking-[0.18em] text-blush-600 hover:bg-blush-50/40 transition"
                data-testid="product-add-image"
              >
                + Add image
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={f.active}
              onChange={(e) => update("active", e.target.checked)}
            />
            <label htmlFor="active" className="font-body text-sm">Active (visible on storefront)</label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-blush-100">
            <button type="button" onClick={onClose} className="btn-ghost-rose">Cancel</button>
            <button type="submit" disabled={saving} className="btn-maroon disabled:opacity-60" data-testid="admin-product-save">
              {saving ? "Saving…" : (isNew ? "Create product" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, type = "text", textarea, step }) {
  return (
    <div className={textarea ? "col-span-full" : ""}>
      <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} className="field-area mt-2" rows={3} placeholder={placeholder} />
      ) : (
        <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className="field mt-2" />
      )}
    </div>
  );
}
